import React, { ReactNode, useMemo, useRef, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Tooltip from "common/Tooltips/Tooltip";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useMeasure from "utils/hooks/useMeasure";

export interface CoinAllocationItem {
  name: string;
  /** Share of the whole, in percent. */
  value: number;
}

interface NewLookCoinAllocationProps<T extends CoinAllocationItem> {
  items: T[];
  /** Rows under the name in a slice's tooltip (e.g. total, available). */
  getDetails?: (item: T) => { name: ReactNode; value: ReactNode }[];
  loading?: boolean;
  /** An account whose balance can't be read: an empty track, no legend. */
  unavailable?: boolean;
}

const NAMED_SLICES = 5;
const LEGEND_GAP = 12;
const COUNT_WIDTH = 28;

let measureContext: CanvasRenderingContext2D | null | undefined;
const textWidth = (text: string, element: HTMLElement | null) => {
  if (measureContext === undefined)
    measureContext = document.createElement("canvas").getContext("2d");
  if (!measureContext || !element) return text.length * 7.5;
  const style = getComputedStyle(element);
  measureContext.font = `500 12px ${style.fontFamily}`;
  return measureContext.measureText(text).width * 1.04 + 2;
};

const formatShare = (value: number) =>
  `${value < 1 ? Math.round(value * 10) / 10 : Math.round(value)}%`;

/**
 * An account's coins as one slim bar (the Dashboard's allocation bar) with a
 * legend of the largest slices. A slice and its legend entry light up
 * together, from either side.
 */
const NewLookCoinAllocation = <T extends CoinAllocationItem>({
  items,
  getDetails,
  loading,
  unavailable,
}: NewLookCoinAllocationProps<T>) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const [measureLegend, legendSize] = useMeasure<HTMLDivElement>();
  const legendElement = useRef<HTMLDivElement | null>(null);
  const [hovered, setHovered] = useState<number>();
  const palette = theme.colors.pieChartColors;

  const slices = useMemo(() => {
    const visible = items.filter((item) => item.value > 0.1);
    const named = visible.slice(0, NAMED_SLICES).map((item, index) => ({
      key: `${index}-${item.name}`,
      name: item.name,
      value: item.value,
      color: palette[index % palette.length],
      item: item as T | undefined,
    }));
    const rest = visible.slice(NAMED_SLICES);
    if (!rest.length) return named;
    return [
      ...named,
      {
        key: "other",
        name: t("labels.other"),
        value: rest.reduce((sum, item) => sum + item.value, 0),
        color: tokens.muted,
        item: undefined,
      },
    ];
  }, [items, palette, tokens.muted, t]);

  const legendCount = useMemo(() => {
    const width = legendSize?.width;
    if (!width) return Math.min(3, slices.length);
    let used = 0;
    let count = 0;
    for (const slice of slices) {
      const itemWidth =
        13 +
        textWidth(
          `${slice.name} ${formatShare(slice.value)}`,
          legendElement.current
        ) +
        (count ? LEGEND_GAP : 0);
      const reserve = count + 1 < slices.length ? COUNT_WIDTH : 0;
      if (used + itemWidth + reserve > width) break;
      used += itemWidth;
      count += 1;
    }
    return count;
  }, [legendSize?.width, slices]);

  if (loading)
    return (
      <Wrapper aria-busy="true">
        <Skeleton width="100%" height={8} radius={4} />
        <Legend>
          <Skeleton width={64} height={12} />
          <Skeleton width={56} height={12} />
          <Skeleton width={60} height={12} />
        </Legend>
      </Wrapper>
    );

  if (unavailable || !slices.length)
    return (
      <Wrapper>
        <Track />
      </Wrapper>
    );

  const hiddenCount = slices.length - legendCount;
  const renderTooltip = (slice: (typeof slices)[number]) => (
    <TooltipBody>
      <TooltipTitle>
        {slice.name} · {formatShare(slice.value)}
      </TooltipTitle>
      {slice.item &&
        getDetails?.(slice.item).map((row, index) => (
          <TooltipRow key={index}>
            <span>{row.name}</span>
            <span>{row.value}</span>
          </TooltipRow>
        ))}
    </TooltipBody>
  );

  return (
    <Wrapper onMouseLeave={() => setHovered(undefined)}>
      <Bar
        role="img"
        aria-label={slices
          .map((slice) => `${slice.name} ${formatShare(slice.value)}`)
          .join(", ")}
      >
        {slices.map((slice, index) => (
          <Tooltip
            key={slice.key}
            title={renderTooltip(slice)}
            mouseEnterDelay={0}
          >
            <Segment
              $dimmed={hovered !== undefined && hovered !== index}
              style={{ flexGrow: slice.value, background: slice.color }}
              onMouseEnter={() => setHovered(index)}
            />
          </Tooltip>
        ))}
      </Bar>
      <Legend
        ref={(node: HTMLDivElement | null) => {
          legendElement.current = node;
          if (node) measureLegend(node);
        }}
      >
        {slices.slice(0, legendCount).map((slice, index) => (
          <LegendItem
            key={slice.key}
            $dimmed={hovered !== undefined && hovered !== index}
            onMouseEnter={() => setHovered(index)}
          >
            <LegendDot style={{ background: slice.color }} />
            {slice.name}
            <Share>{formatShare(slice.value)}</Share>
          </LegendItem>
        ))}
        {hiddenCount > 0 && <More>+{hiddenCount}</More>}
      </Legend>
    </Wrapper>
  );
};

export default NewLookCoinAllocation;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;
const Bar = styled.div`
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
`;
const Track = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Segment = styled.span<{ $dimmed: boolean }>`
  flex-basis: 0;
  min-width: 3px;
  opacity: ${(p) => (p.$dimmed ? 0.3 : 1)};
  transition: opacity 150ms;
`;
const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: ${LEGEND_GAP}px;
  min-width: 0;
  min-height: 16px;
  overflow: hidden;
`;
const LegendItem = styled.span<{ $dimmed: boolean }>`
  display: inline-flex;
  align-items: center;
  flex: none;
  ${newLookType.meta};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
  transition: opacity 150ms;
  ${(p) =>
    p.$dimmed &&
    css`
      opacity: 0.45;
    `}
`;
const LegendDot = styled.span`
  width: 7px;
  height: 7px;
  margin-right: 6px;
  border-radius: 2px;
`;
const Share = styled.span`
  margin-left: 4px;
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const More = styled.span`
  margin-left: auto;
  ${newLookType.meta};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const TooltipBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
  font-size: 12px;
  line-height: 16px;
  font-variant-numeric: tabular-nums;
`;
const TooltipTitle = styled.div`
  font-weight: 520;
`;
const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  > :first-child {
    opacity: 0.72;
  }
`;
